/**
 * DZI 準拠タイルピラミッドの幾何（純関数・FR-ZOOM-02）。
 *
 * ★ server/src/Imaging/TileGeometry.php と同じ数式。サーバが切り出し、クライアントが
 *   合成するため、数式がずれると継ぎ目・404 として現れる。両側の単体テストは同一の
 *   期待値表で縛られている（client/test/source/image-tiles/dzi.test.ts）。
 *
 * 標準 DZI からの唯一の逸脱: レベル全体が 1 タイルに収まる段より下は冗長なので生成
 * しない（minLevel）。
 */
import type { PageRegion } from '../../core/source';

/** ページ 1 枚分のピラミッド定義（マニフェストの imaging + pages[].image/tiles から組む）。 */
export interface Pyramid {
  /** フル解像度のピクセル寸法。 */
  width: number;
  height: number;
  tileSize: number;
  overlap: number;
  minLevel: number;
  maxLevel: number;
}

export interface LevelSize {
  width: number;
  height: number;
}

export interface Grid {
  cols: number;
  rows: number;
}

/** タイル 1 枚が保持する矩形（レベル座標・overlap 込み）。 */
export interface TileRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 領域が交差するタイルの範囲（両端含む）。 */
export interface TileRange {
  col0: number;
  col1: number;
  row0: number;
  row1: number;
}

/** 最上位（フル解像度）レベル。2^level が長辺以上になる最小の level。 */
export function maxLevelFor(width: number, height: number): number {
  const longest = Math.max(width, height);
  let level = 0;
  for (let span = 1; span < longest; span *= 2) level++;
  return level;
}

/** 生成する最下位レベル。全体が 1 タイルに収まる最大の level（それ未満は冗長）。 */
export function minLevelFor(width: number, height: number, tileSize: number): number {
  const max = maxLevelFor(width, height);
  for (let level = max; level > 0; level--) {
    const size = levelSizeFor(width, height, max, level);
    if (Math.max(size.width, size.height) <= tileSize) return level;
  }
  return 0;
}

/** マニフェストの値からピラミッドを組む（minLevel/maxLevel はサーバの申告を正とする）。 */
export function pyramid(input: {
  width: number;
  height: number;
  tileSize: number;
  overlap: number;
  maxLevel: number;
  minLevel: number;
}): Pyramid {
  return { ...input };
}

/** レベル level の画像寸法。1/2 ずつ縮小し、端数は切り上げる。 */
export function levelSize(p: Pyramid, level: number): LevelSize {
  return levelSizeFor(p.width, p.height, p.maxLevel, level);
}

function levelSizeFor(
  width: number,
  height: number,
  maxLevel: number,
  level: number,
): LevelSize {
  const scale = 2 ** (maxLevel - level);
  return {
    width: Math.max(1, Math.ceil(width / scale)),
    height: Math.max(1, Math.ceil(height / scale)),
  };
}

/** レベル level のタイル格子。 */
export function grid(p: Pyramid, level: number): Grid {
  const size = levelSize(p, level);
  return {
    cols: Math.ceil(size.width / p.tileSize),
    rows: Math.ceil(size.height / p.tileSize),
  };
}

/**
 * タイル (col, row) が保持する矩形（レベル座標・overlap 込み）。
 * overlap は画像の外側へは出ないため、端のタイルでは片側にしか付かない。
 */
export function tileRect(p: Pyramid, level: number, col: number, row: number): TileRect {
  const size = levelSize(p, level);
  const x = Math.max(0, col * p.tileSize - p.overlap);
  const y = Math.max(0, row * p.tileSize - p.overlap);
  const right = Math.min(size.width, (col + 1) * p.tileSize + p.overlap);
  const bottom = Math.min(size.height, (row + 1) * p.tileSize + p.overlap);
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * ページ全面換算の目標幅を満たす最小のレベル。
 * 目標がフル解像度を超えるときは maxLevel で頭打ち（＝拡大上限 = 生成解像度）。
 */
export function levelForWidth(p: Pyramid, wantWidth: number): number {
  for (let level = p.minLevel; level < p.maxLevel; level++) {
    if (levelSize(p, level).width >= wantWidth) return level;
  }
  return p.maxLevel;
}

/** 領域（正規化座標）が交差するタイルの範囲。 */
export function tileRange(p: Pyramid, level: number, region: PageRegion): TileRange {
  const size = levelSize(p, level);
  const { cols, rows } = grid(p, level);
  const clampCol = (v: number): number => Math.min(cols - 1, Math.max(0, v));
  const clampRow = (v: number): number => Math.min(rows - 1, Math.max(0, v));
  const left = region.x * size.width;
  const top = region.y * size.height;
  const right = (region.x + region.w) * size.width;
  const bottom = (region.y + region.h) * size.height;
  const col0 = clampCol(Math.floor(left / p.tileSize));
  const row0 = clampRow(Math.floor(top / p.tileSize));
  return {
    col0,
    row0,
    col1: Math.max(col0, clampCol(Math.ceil(right / p.tileSize) - 1)),
    row1: Math.max(row0, clampRow(Math.ceil(bottom / p.tileSize) - 1)),
  };
}
